import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  userForgotPassword,
  resetStatus,
} from "features/common/auth/authSlice";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import { notifyWarning, notifySuccess, notifyError } from "components/ToastMessage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faSpinner, faCheck } from "@fortawesome/free-solid-svg-icons";

const ModalForgotPassword = (props) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  
  // States
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [step, setStep] = useState(1); // 1 = send OTP, 2 = verify OTP & reset password
  const [submitDisable, setSubmitDisable] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);

  // OTP Countdown Timer
  useEffect(() => {
    let interval = null;
    if (otpCountdown > 0) {
      interval = setInterval(() => {
        setOtpCountdown(countdown => countdown - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpCountdown]);

  const handlerForgot = (e) => {
    if (!email) {
      notifyWarning("Email Anda kosong!");
      return;
    }
    setSubmitDisable(true);
    dispatch(userForgotPassword(email));
    e.preventDefault();
  };

  const handlerVerifyAndReset = async (e) => {
    e.preventDefault();
    
    // Validations
    if (!otp || otp.length !== 6) {
      notifyWarning("Masukkan kode OTP 6 digit!");
      return;
    }
    
    if (!newPassword || newPassword.length < 8) {
      notifyWarning("Password baru minimal 8 karakter!");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      notifyWarning("Konfirmasi password tidak sama!");
      return;
    }
    
    setIsVerifying(true);
    
    try {
      const formData = new FormData();
      formData.append('action', 'verify_reset');
      formData.append('email', email);
      formData.append('otp', otp);
      formData.append('new_password', newPassword);
      formData.append('confirm_password', confirmPassword);
      
      const response = await fetch(`${process.env.REACT_APP_BASE_API}order/apis/forgotPasswordAccount.php`, {
        method: 'POST',
        body: formData
      });
      
      const result = await response.json();
      
      if (result.success) {
        notifySuccess(result.message || "Password berhasil diubah! Silakan login dengan password baru.");
        handleCloseModal();
      } else {
        notifyError(result.error || "Gagal mengubah password. Silakan coba lagi.");
      }
      
    } catch (error) {
      notifyError("Terjadi kesalahan. Silakan coba lagi.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCloseModal = () => {
    // Reset all states
    setEmail("");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setStep(1);
    setSubmitDisable(false);
    setOtpCountdown(0);
    setIsVerifying(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    props.onHide();
  };

  const handleResendOtp = () => {
    setSubmitDisable(true);
    dispatch(userForgotPassword(email));
  };

  useEffect(() => {
    if (auth.status === "fulfilled") {
      setSubmitDisable(false);
      if (step === 1) {
        notifySuccess("Kode OTP telah dikirim ke email Anda");
        setStep(2);
        setOtpCountdown(300); // 5 minutes
      }
      dispatch(resetStatus("idle"));
    } else if (auth.status === "error" && props.show) {
      setSubmitDisable(false);
      // Handle new API response structure more robustly
      let errorMessage = "Terjadi kesalahan saat mengirim email reset password";
      
      if (auth.error) {
        // Handle direct API response structure: {success: false, error: "message"}
        if (typeof auth.error === 'string') {
          errorMessage = auth.error;
        } 
        // Handle nested error in response payload
        else if (auth.error.error && typeof auth.error.error === 'string') {
          errorMessage = auth.error.error;
        }
        // Handle message in error object
        else if (auth.error.message && typeof auth.error.message === 'string') {
          errorMessage = auth.error.message;
        }
        // Handle nested error with message
        else if (auth.error.error && auth.error.error.message) {
          errorMessage = auth.error.error.message;
        }
      }
      
      notifyError(errorMessage);
      dispatch(resetStatus("idle"));
    } else if (auth.status === "idle") {
      setSubmitDisable(false);
    }
  }, [dispatch, auth.status, auth.error, props.show, step]);

  return (
    <>
      {/* modal for forgot password */}
      <Modal
        show={props.show}
        onHide={handleCloseModal}
        backdrop="static"
        keyboard={false}
        size="sm"
        aria-labelledby="example-custom-modal-styling-title"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="example-custom-modal-styling-title">
            Reset Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {step === 1 ? (
            // Step 1: Send OTP
            <div>
              <p>
                Lupa Password akun anda? Masukan Alamat email dibawah ini untuk
                mendapatkan kode OTP reset password.
              </p>
              <div className="mb-3">
                <label htmlFor="email" className="form-label label-qwords">
                  Masukkan Email
                </label>
                <input
                  type="email"
                  className="form-control input-modal"
                  id="email"
                  aria-describedby="emailHelp"
                  placeholder="email@qwords.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
          ) : (
            // Step 2: Verify OTP & Reset Password
            <div>
              <p>
                Kode OTP telah dikirim ke email <strong>{email}</strong>. 
                Masukkan kode OTP dan password baru Anda.
              </p>
              
              {/* OTP Input */}
              <div className="mb-3">
                <label htmlFor="otp" className="form-label label-qwords">
                  Kode OTP (6 digit)
                </label>
                <input
                  type="text"
                  className="form-control input-modal text-center"
                  id="otp"
                  placeholder="Masukan OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                />
                {otpCountdown > 0 ? (
                  <small className="text-muted">
                    Kode berlaku {Math.floor(otpCountdown / 60)}:{(otpCountdown % 60).toString().padStart(2, '0')}
                  </small>
                ) : (
                  <small className="text-warning">
                    Kode OTP sudah kadaluarsa
                  </small>
                )}
              </div>

              {/* New Password */}
              <div className="mb-3">
                <label htmlFor="newPassword" className="form-label label-qwords">
                  Password Baru
                </label>
                <div className="input-group">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    className="form-control input-modal"
                    id="newPassword"
                    placeholder="Minimal 8 karakter"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="mb-3">
                <label htmlFor="confirmPassword" className="form-label label-qwords">
                  Konfirmasi Password Baru
                </label>
                <div className="input-group">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="form-control input-modal"
                    id="confirmPassword"
                    placeholder="Ulangi password baru"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              {/* Resend OTP */}
              <div className="text-center mb-3">
                <button
                  type="button"
                  className={`btn btn-sm ${otpCountdown > 0 ? 'btn-outline-secondary' : 'btn-warning'}`}
                  onClick={handleResendOtp}
                  disabled={submitDisable || otpCountdown > 0}
                >
                  {submitDisable ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                      Mengirim...
                    </>
                  ) : otpCountdown > 0 ? (
                    'Kirim Ulang OTP'
                  ) : (
                    '🔄 Kirim Ulang OTP'
                  )}
                </button>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="row col-12">
            {step === 1 ? (
              <button
                className="btn btn-modal-login"
                onClick={handlerForgot}
                disabled={submitDisable}
              >
                {auth.status === "loading" ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Kirim Kode OTP"
                )}
              </button>
            ) : (
              <button
                className="btn btn-modal-login"
                onClick={handlerVerifyAndReset}
                disabled={isVerifying}
              >
                {isVerifying ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Mengubah Password...
                  </>
                ) : (
                  "Ubah Password"
                )}
              </button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ModalForgotPassword;
